import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { X, CheckCircle, ChatCircleDots, Sparkle, Video, HouseSimple, UsersThree } from '@phosphor-icons/react';
import { MODES } from '@/data/wellness';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODE_ICON = { online: Video, in_person: UsersThree, home_visit: HouseSimple };

const ConsultationRequestModal = ({ open, onClose, pro, user }) => {
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [form, setForm] = useState({
    preferred_mode: pro?.modes?.[0] || 'online',
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    goal: '',
    preferred_dates: ['', ''],
    message: '',
  });

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const setDate = (i) => (e) => setForm((s) => {
    const dates = [...s.preferred_dates];
    dates[i] = e.target.value;
    return { ...s, preferred_dates: dates };
  });

  const handleClose = () => {
    setConfirmation(null);
    setForm({
      preferred_mode: pro?.modes?.[0] || 'online',
      customer_name: user?.name || '', customer_email: user?.email || '', customer_phone: '',
      goal: '', preferred_dates: ['', ''], message: '',
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.goal) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        professional_id: pro.professional_id,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        goal: form.goal,
        preferred_mode: form.preferred_mode,
        preferred_dates: form.preferred_dates.filter(Boolean),
        message: form.message,
      };
      const res = await axios.post(`${BACKEND_URL}/api/wellness/consultation-requests`, payload);
      setConfirmation(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-white border border-stone-200 focus:border-[#1A4331] focus:ring-4 focus:ring-[#1A4331]/10 rounded-2xl px-4 py-3 outline-none transition-all text-[15px] text-[#1C1917] placeholder:text-stone-400";
  const labelCls = "block text-[11px] font-bold uppercase tracking-[0.14em] text-stone-500 mb-1.5";

  if (!pro) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={handleClose} data-testid="consultation-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: 'spring', damping: 26, stiffness: 260 }} className="relative w-full max-w-lg bg-white rounded-[2rem] shadow-2xl overflow-hidden my-8" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-gradient-to-br from-[#1A4331] to-[#2A5A44] px-6 pt-6 pb-14 overflow-hidden">
              <div className="pointer-events-none absolute -top-16 -right-16 w-52 h-52 bg-[#34D399]/20 rounded-full blur-3xl" />
              <button onClick={handleClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-all" data-testid="consultation-close-btn">
                <X size={18} weight="bold" />
              </button>
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 text-[#34D399] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] mb-2">
                  <ChatCircleDots size={11} weight="fill" /> Consultation Request
                </div>
                <h2 className="text-xl md:text-2xl text-white font-heading font-medium tracking-tight">
                  {confirmation ? 'Request sent!' : `Talk to ${pro.full_name}`}
                </h2>
              </div>
            </div>

            <div className="relative -mt-8 px-4 pb-6">
              <div className="bg-white rounded-3xl border border-stone-100 shadow-xl shadow-stone-200/40 p-6">
                {confirmation ? (
                  <div className="text-center py-2" data-testid="consultation-success">
                    <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200, damping: 14 }} className="w-20 h-20 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-[#10B981]/30">
                      <CheckCircle size={40} weight="fill" className="text-white" />
                    </motion.div>
                    <h3 className="text-xl font-medium font-heading text-[#1C1917] mb-2 tracking-tight">Consultation request sent</h3>
                    <p className="text-stone-600 text-sm mb-5 max-w-sm mx-auto leading-relaxed">
                      {pro.full_name} typically responds within 24-48 hours. Watch your email at <strong>{form.customer_email}</strong>.
                    </p>
                    <div className="bg-stone-50 rounded-2xl px-5 py-4 mb-5 inline-block">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-1">Request ID</p>
                      <p className="text-lg font-bold text-[#1A4331] font-heading tracking-tight" data-testid="consultation-request-id">{confirmation.request_id}</p>
                    </div>
                    <div>
                      <button onClick={handleClose} className="bg-[#1A4331] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-md" data-testid="consultation-done-btn">
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2.5 rounded-2xl text-xs">
                      <Sparkle size={14} weight="fill" className="flex-shrink-0 mt-0.5 text-emerald-600" />
                      <p>Not sure? Send a free request — the professional reviews your goal &amp; proposes a time before you pay.</p>
                    </div>

                    <div>
                      <label className={labelCls}>What do you need help with? *</label>
                      <input type="text" value={form.goal} onChange={setField('goal')} placeholder="e.g., Back pain management, weight loss…" className={inputCls} required data-testid="consultation-goal" />
                    </div>

                    <div>
                      <label className={labelCls}>Preferred Mode</label>
                      <div className="flex gap-2 flex-wrap">
                        {(pro.modes || []).map((m) => {
                          const M = MODE_ICON[m];
                          return (
                            <button key={m} type="button" onClick={() => setForm({ ...form, preferred_mode: m })} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${form.preferred_mode === m ? 'bg-[#1A4331] text-white border-[#1A4331]' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1A4331]'}`}>
                              {M && <M size={14} weight="duotone" />} {MODES.find((x) => x.id === m)?.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Preferred Date 1</label>
                        <input type="date" value={form.preferred_dates[0]} onChange={setDate(0)} className={inputCls} data-testid="consultation-date-1" />
                      </div>
                      <div>
                        <label className={labelCls}>Preferred Date 2</label>
                        <input type="date" value={form.preferred_dates[1]} onChange={setDate(1)} className={inputCls} data-testid="consultation-date-2" />
                      </div>
                    </div>

                    <div className="pt-2 border-t border-stone-100">
                      <p className={labelCls}>Your Info</p>
                      <input type="text" value={form.customer_name} onChange={setField('customer_name')} placeholder="Full Name *" className={inputCls} required data-testid="consultation-name" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <input type="email" value={form.customer_email} onChange={setField('customer_email')} placeholder="Email *" className={inputCls} required data-testid="consultation-email" />
                        <input type="tel" value={form.customer_phone} onChange={setField('customer_phone')} placeholder="Phone" className={inputCls} />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>Message <span className="text-stone-400 font-normal normal-case tracking-normal">(Optional)</span></label>
                      <textarea rows={3} value={form.message} onChange={setField('message')} placeholder="Anything else the professional should know?" className={`${inputCls} resize-none`} />
                    </div>

                    <button type="submit" disabled={submitting} className="w-full bg-[#1A4331] text-white py-3.5 rounded-full font-semibold hover:bg-[#2A5A44] transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2" data-testid="consultation-submit-btn">
                      {submitting ? 'Sending...' : 'Send Consultation Request'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsultationRequestModal;
